import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild, Renderer2, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { debounceTime, distinctUntilChanged, Subject, Subscription } from 'rxjs';

import {TabMenuModule} from 'primeng/tabmenu';
import {MenuItem} from 'primeng/api';
import {InputTextModule} from 'primeng/inputtext';
import {ButtonModule} from 'primeng/button';
import {TableModule} from 'primeng/table';
import {Paginator, PaginatorModule} from 'primeng/paginator';
import {CheckboxModule} from 'primeng/checkbox';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TooltipModule } from 'primeng/tooltip';

import { ViewWorkers, WorkerService } from '@layout/driver/services/worker.service';
import { Driver } from '@commons/models/domain/Driver';
import { exportWorkersToXLS } from '@src/app/utils/export-files';
import { NewNavigationService } from '@shared/services/new-navigation.service';
import { DriverLicesnseStatus } from '@src/app/enum/driver-license.enum';
import { PermissionService } from '@shared/services/permission.service';
import { FilterControlComponent } from '@shared/components/vehicle-list/filter-control/filter-control.component';
import { IFilterVehicle, ISubmitFilters } from '@src/app/interfaces/filters-vehicle.interface';
import { FilterVehicleType } from '@src/app/enum/filters-vehicle.enum';
import { DriverStatusToTextPipe } from '@commons/pipes/driver-status-to-text.pipe';
import { LanguageService } from '@shared/services/language.service';

@Component({
  selector: 'app-workers',
  standalone: true,
  imports: [CommonModule, TabMenuModule, DriverStatusToTextPipe, TranslateModule, FilterControlComponent, FormsModule, TooltipModule, InputTextModule, ButtonModule, TableModule, PaginatorModule, CheckboxModule, ReactiveFormsModule],
  templateUrl: './workers.component.html',
  styleUrls: ['./workers.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WorkersComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('containerList') private containerListRef: ElementRef<HTMLDivElement>;
  @ViewChild('paginator') private paginatorRef: Paginator;

  @Output() public onRedirectToFormWorker$: EventEmitter<Driver> = new EventEmitter();

  public activeView: ViewWorkers = ViewWorkers.Active;
  public tabMenu: MenuItem[] = [
    {label: this.translateService.instant('worker.active'), command: () => this.onChangeView(ViewWorkers.Active) },
    {label: this.translateService.instant('worker.archive'), command: () => this.onChangeView(ViewWorkers.Archive) },
  ];
  public activeTabOption: MenuItem = this.tabMenu[0];
  public isActiveDriverLicenseColumn: boolean = this.permissionService.hasModule_CheckDriverLicence();

  public isVisibleList: boolean;
  public listHeight: string;
  public isCheckedAll: boolean = false;
  public rowsPerPage: number = 20;
  public totalRecordsDisplayedList: number = 0;

  public searchWorkersValue: string;
  public searchWorkersValue$: Subject<string> = new Subject();
  public filtersDriverLicenseStatusConfig: IFilterVehicle;
  public filterDriverLicenseStatusCurrentValue: ISubmitFilters<DriverLicesnseStatus[]>;

  public currentListPageData: { data: Driver, isChecked: boolean }[] = [];
  public filteredAllWorkersList: Driver[] = [];

  private readonly ComponentSubsc = new Subscription();
  public readonly VIEW = ViewWorkers;
  public readonly DRIVER_LICENSE_STATUS = DriverLicesnseStatus;

  public ChartConfig: any;

  constructor(
    public workerService: WorkerService,
    private changeDetRef: ChangeDetectorRef,
    private navigationService: NewNavigationService,
    public translateService: TranslateService,
    private rendere2: Renderer2,
    private permissionService: PermissionService,
    public languageService: LanguageService,
  ) {
    this.setConfigFilters();
  }

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    this.onChangeSearchValue();
    this.setListHeight();
    this.updateCurrentPageData(0);
  }

  ngOnDestroy(): void {
    this.ComponentSubsc.unsubscribe();
  }

  public onSubmitFilters(data: ISubmitFilters, _: IFilterVehicle): void {
    this.filterDriverLicenseStatusCurrentValue = {...data};
    this.paginatorRef.changePage(0);
  }

  public openWorkerForm(data?: Driver): void {
    this.onRedirectToFormWorker$.emit(data);
  }

  public exportFiles(): void {
    const listWorkers = this.workerService.getAllFilteredList?.length
      ? this.workerService.getAllFilteredList
      : (this.activeView === ViewWorkers.Active ? this.workerService.allWorkers : this.workerService.archivedDrivers);

    if (listWorkers?.length) {
      exportWorkersToXLS(listWorkers, this.activeView, this.translateService);
    }
  }

  public onChangePaginate(event: any): void {
    this.isCheckedAll = false;
    const pageIndex = event.page;
    this.rowsPerPage = event.rows;
    this.updateCurrentPageData(pageIndex);
  }

  private updateCurrentPageData(pageIndex: number): void {
    const body = {
      view: this.activeView,
      pagination: { pageIndex, rows: this.rowsPerPage },
      searchValue: this.searchWorkersValue,
      filterDriverLicenseStatuses: this.filterDriverLicenseStatusCurrentValue?.selectedControlValue,
    };
    const data = this.workerService.getListData(body);
    this.totalRecordsDisplayedList = data.totalRecordsInList;
    this.currentListPageData = data.listData;
    this.changeDetRef.markForCheck();
  }

  public onChangeValueCheckedAll(value: boolean): void {
    this.currentListPageData.forEach((item) => {
      if (!item.data.U) {
        item.isChecked = value;
      }
    });
  }

  public countCheckedRow(): number {
    return this.currentListPageData.filter(item => item.isChecked)?.length;
  }

  private setListHeight(): void {
    this.listHeight = (this.containerListRef.nativeElement.clientHeight - 28) + 'px';
    this.rendere2.setStyle(document.getElementsByClassName('p-datatable-table-container').item(0), 'min-height', this.listHeight);
    this.isVisibleList = true;
  }

  private onChangeSearchValue(): void {
    const sub = this.searchWorkersValue$
    .pipe(
      distinctUntilChanged(),
      debounceTime(200),
    )
    .subscribe(() => {
      if (this.paginatorRef.currentPage() === 0) {
        this.onChangePaginate({ page: 0, rows: this.rowsPerPage });
        return;
      }
      this.paginatorRef.changePage(0);
    });
    this.ComponentSubsc.add(sub);
  }

  private onChangeView(view: ViewWorkers): void {
    this.searchWorkersValue = '';
    this.searchWorkersValue$.next('');
    this.activeView = view;
    this.paginatorRef.changePage(0);
  }

  public onWorkersArchived(): void {
    const listToArchive = this.currentListPageData.filter(item => item.isChecked).map(el => el.data);
    this.workerService.archiveWorkers(listToArchive);
    this.onChangePaginate({ page: this.paginatorRef.getPage(), rows: this.rowsPerPage });
  }

  public onWorkersRestore(): void {
    const listToRestore = this.currentListPageData.filter(item => item.isChecked).map(el => el.data);
    this.workerService.restoreWorkers(listToRestore);
    this.onChangePaginate({ page: this.paginatorRef.getPage(), rows: this.rowsPerPage });
  }

  private setConfigFilters(): void {
    this.filtersDriverLicenseStatusConfig = {
      filterLabel: this.translateService.instant('driverLicense'),
      type: FilterVehicleType.MultipleCheckbox,
      dropdownLabel: this.translateService.instant('driverLicenseStatusText'),
      options: [
        { label: this.translateService.instant('driverLicenseStatus.active'), value: DriverLicesnseStatus.Valid },
        { label: this.translateService.instant('driverLicenseStatus.suspended'), value: DriverLicesnseStatus.Invalid },
        { label: this.translateService.instant('driverLicenseStatus.noData'), value: DriverLicesnseStatus.DocumentNotFound },
        { label: this.translateService.instant('driverLicenseStatus.notCheckedYet'), value: DriverLicesnseStatus.NotCheckedYet },
      ],
    };
  }

}
